export const translations = {
  en: {
    language: {
      english: "English",
      hindi: "Hindi",
      switchLabel: "Language",
    },
    common: {
      back: "Back",
      backToDashboard: "Back to Dashboard",
      dashboard: "Dashboard",
      login: "Login",
      logout: "Logout",
      register: "Register",
      registerSchool: "Register School",
      selectClass: "Select Class",
      selectSection: "Select Section",
      selectSubject: "Select Subject",
      date: "Date",
      loadingPermissions: "Loading permissions...",
      clickToAccess: "Click to access",
      more: "more...",
      accessLevel: "Access Level",
      signOut: "Sign Out",
      email: "Email",
      phone: "Phone",
      website: "Website",
    },
    auth: {
      emailUsername: "Email/Username",
      password: "Password",
      forgotPassword: "Forgot password?",
      noAccountRegister: "Don't have an account? Register",
      allFieldsRequired: "All fields are required!",
      sessionExpired: "Session expired. Please log in again.",
      failedLogin: "Failed to login: {error}",
      networkError: "Network error. Please check your internet connection.",
      forgotTitle: "Forgot Password",
      forgotHelp: "Enter your email to receive a password reset link.",
      sending: "Sending...",
      sendResetLink: "Send Reset Link",
      resetTitle: "Reset Password",
      resetHelp: "Enter your new password below.",
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      passwordsDoNotMatch: "Passwords do not match",
      resetting: "Resetting...",
      resetError: "Error resetting password. Make sure the reset link is latest",
      resetEmailError: "Error sending reset email.",
    },
    register: {
      title: "Register",
      settingUpSchool: "Setting up school",
      creatingClasses: "Creating classes with section A...",
      academicSetupFailed: "Academic setup failed. Please try again.",
      academicSetupCompleted: "Academic setup completed.",
      schoolSetupCompleted: "School setup completed!",
      creatingSchoolAccount: "Creating school account...",
      schoolRegisteredSetupCouldNotStart: "School registered, but setup could not start.",
      failedToRegister: "Failed to register school. Please try again.",
      genericError: "An error occurred. Please check your network.",
      schoolRegisteredSettingUp: "School registered. Setting up academics...",
      usuallyTakesSeconds: "This usually takes a few seconds.",
      retryAcademicSetup: "Retry academic setup",
      schoolName: "School Name",
      affiliationNumber: "School Affiliation Number",
      educationBoard: "Education Board",
      classesTill: "Classes Till",
      classLabel: "Class {number}",
      medium: "Medium",
      adminName: "Admin Name",
      mediumHindi: "Hindi",
      mediumEnglish: "English",
    },
    thankyou: {
      title: "Thank You!",
      submitted: "Your registration form has been submitted successfully.",
      followUp: "We will get back to you shortly.",
      backToLogin: "Go back to login",
    },
    landing: {
      lx: {
        nav: {
          story: "Why SpellStudy",
          platform: "Platform",
          aiDemo: "AI in Action",
          roles: "For You",
        },
        hero: {
          kicker: "The School Operating System for Indian Schools",
          title1: "Your Entire School.",
          title2: "One Living Dashboard.",
          subtitle:
            "Attendance, fees, homework, exams, parent communication, and an AI assistant — everything your school runs on, connected in one place. In English and Hindi.",
          ctaDemo: "Book a Free Demo",
          ctaWhatsapp: "Say Hi on WhatsApp",
          scrollCue: "Scroll to see how",
        },
        chaos: {
          kicker: "A Normal School Morning",
          title: "Running a school shouldn't feel like",
          titleHighlight: "controlled chaos.",
          subtitle:
            "Registers in one room, Excel in another, fees in a diary, and parents across twenty WhatsApp groups. Keep scrolling — watch what happens when it all comes together.",
          cards: [
            { title: "Paper Registers", tag: "Attendance, records — all manual" },
            { title: "Excel Sheets", tag: "One wrong cell, hours lost" },
            { title: "WhatsApp Groups", tag: "Notices lost in 40 chats" },
            { title: "Fee Dues Diary", tag: "Who paid? Who didn't?" },
            { title: "Notice Photocopies", tag: "Half never reach home" },
            { title: "Report Card Nights", tag: "Teachers up till 2 AM" },
          ],
          resolve: "One scroll. That's how fast it feels when everything lives in one place.",
        },
        pillars: {
          kicker: "The Platform",
          title: "Five pillars. Zero registers.",
          items: [
            {
              title: "Attendance & Classes",
              desc: "Classwise attendance and school status, live on the principal's screen by 9:05 AM.",
            },
            {
              title: "Fees & Collections",
              desc: "Today's collection, pending dues, and defaulter follow-ups — one finance dashboard.",
            },
            {
              title: "Academics & Progress",
              desc: "Every student's marks, homework, and growth patterns — spot who needs help early.",
            },
            {
              title: "Teachers & Communication",
              desc: "Homework, notices, and results reach every parent's phone — no follow-up calls.",
            },
            {
              title: "AI Reports & Insights",
              desc: "Ask a question, get a report. In Hindi or English. In seconds.",
            },
          ],
        },
        ai: {
          kicker: "The Wow Moment",
          title: "Meet your school's",
          titleHighlight: "AI assistant.",
          subtitle:
            "This is a real recording — watch the AI assistant handle school work exactly the way your team would ask for it.",
          chips: [
            "Ask in Hindi or English",
            "Any report in seconds",
            "“Fee defaulters of Class 8?” — answered",
            "Simple enough for every staff member",
          ],
          caption: "Live footage from SpellStudy — not a mockup.",
          unmute: "Unmute",
          mute: "Mute",
          replay: "Replay",
          fullscreen: "Fullscreen",
          exitFullscreen: "Exit fullscreen",
        },
        roles: {
          kicker: "Built for Your Chair",
          title: "Whoever you are in the school, this is for you.",
          subtitle:
            "Owners, principals, teachers, and parents see different screens — but the same connected truth.",
          cards: [
            {
              title: "Owners & Directors",
              hook: "Total control, from anywhere",
              points: [
                "Fee collection at a glance",
                "Every campus, one login",
                "Daily operations without calls",
                "Your data stays yours",
              ],
            },
            {
              title: "Principals",
              hook: "Your school on one screen",
              points: [
                "Classwise attendance by 9 AM",
                "Academic progress tracking",
                "Staff & compliance view",
                "Fewer fires to fight",
              ],
            },
            {
              title: "Teachers",
              hook: "Teach more, type less",
              points: [
                "Attendance in seconds",
                "Homework in two taps",
                "Report cards without late nights",
                "Notices that actually reach",
              ],
            },
            {
              title: "Parents",
              hook: "Trust, delivered daily",
              points: [
                "Attendance & homework updates",
                "Fee receipts on the phone",
                "Results as they publish",
                "One app, no group noise",
              ],
            },
          ],
          counters: [
            { value: 48, suffix: " hrs", label: "From details shared to your first live class" },
            { value: 1, suffix: " class", label: "Is all it takes to start the pilot" },
            { value: 10, suffix: " min", label: "Demo — see it before you decide" },
            { value: 24, suffix: " hrs", label: "Maximum response time from our team" },
          ],
        },
        journey: {
          kicker: "No Risky Migration",
          title: "From demo to live in",
          titleHighlight: "one week.",
          subtitle:
            "Start with a single class. No full-school migration, no disruption — expand only when your teachers ask you to.",
          steps: [
            { day: "Day 0", title: "10-minute demo", desc: "See SpellStudy over a cup of chai — on call or WhatsApp." },
            { day: "Day 1–2", title: "One class set up", desc: "We import your students, teachers, and class structure for you." },
            { day: "Day 3", title: "Teachers trained", desc: "Hands-on training — attendance, homework, notices in minutes." },
            { day: "Day 7", title: "Parents go live", desc: "Parents start seeing updates on their phones. The follow-up calls stop." },
          ],
          cta: "Start My One-Class Pilot",
        },
        cosmos: {
          mission: "Mission: Earth → Sun",
          earth: { name: "Earth", tag: "Launch pad — your school today" },
          moon: { name: "The Moon", tag: "The dark side of paperwork" },
          mars: { name: "Mars", tag: "Five pillars of a new world" },
          jupiter: { name: "Jupiter", tag: "Giant power, tiny effort" },
          saturn: { name: "Saturn", tag: "Every role in perfect orbit" },
          venus: { name: "Venus", tag: "A smooth 7-day landing" },
          mercury: { name: "Mercury", tag: "Quick answers, short orbits" },
          sun: { name: "The Sun", tag: "Where your school shines" },
        },
        finale: {
          title1: "Your school deserves",
          title2: "better than registers.",
          subtitle:
            "Join the schools building their next decade on SpellStudy. A 10-minute demo is all it takes to see the difference.",
          ctaDemo: "Book My Free Demo",
          ctaWhatsapp: "Say Hi on WhatsApp",
          footnote: "Free demo • One-class pilot • Setup support included • Made in India",
        },
      },
      nav: {
        pilot: "Pilot",
        roles: "Roles",
        product: "Product",
        howItWorks: "How It Works",
        contact: "Contact",
        login: "Login",
        bookDemo: "Book Free Demo",
        openNavigation: "Open navigation",
        oneClassPilot: "One-Class Pilot",
        roleBenefits: "Role Benefits",
        productScreenshots: "Product Screenshots",
        whatsAppDemo: "WhatsApp for Demo",
      },
      launch: {
        offer: "Special launch offer: Free setup for first 100 schools. Start with one class.",
        bookDemo: "Book Free Demo",
        whatsAppDemo: "WhatsApp for Demo",
      },
      hero: {
        badge: "Built for Indian Schools",
        titlePrefix: "One Platform to Manage",
        titleHighlight: "School Operations and Student Progress",
        subtitle:
          "Manage attendance, homework, fees, notices, reports, parent communication, and individual student progress from one simple dashboard and parent mobile app.",
        offer: "Start with one class. Free setup support for selected early adopter schools. No full-school migration required",
        bookDemo: "Book Free 10-Min Demo",
        whatsAppDemo: "WhatsApp for Demo",
        trust: "Start with one class • Setup support included • AWS Hosted",
        imageAlt: "SpellStudy Admin Dashboard",
        pillars: [
          "Student Progress Tracking",
          "Attendance & Homework",
          "Fee Follow-Up",
          "Parent Mobile App",
        ],
        playVideo: "Play video",
        closeVideo: "Close video",
        unmute: "Tap for sound",
        mute: "Mute",
        replay: "Replay",
        fullscreen: "Fullscreen",
        exitFullscreen: "Exit fullscreen",
      },
      pilot: {
        badge: "Low-Risk Pilot",
        title: "Try SpellStudy Without Changing Your Full School System",
        subtitle: "Start small, prove parent response and teacher adoption, then expand when your team is ready.",
        cta: "Start Free One-Class Pilot",
        items: [
          "Start with 1 class",
          "Add 2 teachers",
          "Import 20-40 students",
          "Send homework, attendance, and notices",
          "Parents view updates on mobile",
          "Setup support included",
        ],
      },
      roles: {
        badge: "Built for Every School Stakeholder",
        title: "One Platform, Clear Value for Every Role",
        subtitle:
          "Owners and principals get visibility, teachers save time, and parents stay informed without repeated follow-up calls.",
        cards: [
          { title: "For Admin Teams", points: ["Fees", "Student records", "Reports", "Certificates"] },
          { title: "For Teachers", points: ["Attendance", "Homework", "Exams", "Notices"] },
          { title: "For Parents", points: ["Homework", "Attendance", "Fee updates", "Reports"] },
          {
            title: "For Principals & Directors",
            points: ["Dashboard", "School visibility", "Parent satisfaction", "Class progress"],
          },
        ],
      },
      pain: {
        badge: "The Challenge",
        titlePrefix: "Running a School Today is",
        titleHighlight: "Harder Than Ever",
        subtitle:
          "Most schools do not need more software complexity. They need fewer parent follow-ups, faster fee tracking, and one place for daily school operations.",
        cards: [
          {
            title: "Parents Keep Calling for Updates",
            desc:
              "Homework, attendance, notices, fee reminders, and results often require repeated calls or WhatsApp follow-ups from staff.",
          },
          {
            title: "Students Who Need Help Are Spotted Too Late",
            desc:
              "When attendance, homework, tests, and teacher observations are scattered, principals and teachers miss early warning signs until performance drops.",
          },
          {
            title: "Fee Follow-Up Takes Too Much Time",
            desc:
              "Pending fees, daily collection, receipts, and reminders are hard to track when accounts live in registers or spreadsheets.",
          },
          {
            title: "Homework and Notices Get Missed",
            desc:
              "Teachers send updates in different places, parents miss messages, and the school has no clear record of what was shared.",
          },
          {
            title: "Manual Registers Slow Everything Down",
            desc:
              "Student records, attendance, reports, certificates, and class data stay scattered across paper files and Excel sheets.",
          },
          {
            title: "Compliance and Reports Add Pressure",
            desc:
              "When school data is not organized, audits, reports, documents, and compliance work become stressful at the last minute.",
          },
        ],
      },
      features: {
        badge: "The Solution",
        titlePrefix: "Everything Your School Needs,",
        titleHighlight: "One Platform",
        subtitle:
          "SpellStudy replaces manual registers, Excel sheets, and scattered parent messages with one connected school operating system.",
        cards: [
          {
            title: "Collect Fees Faster",
            desc:
              "Track today's collection, pending amount, paid students, overdue accounts, and reminders from one finance dashboard.",
          },
          {
            title: "Reduce Parent Follow-Up Calls",
            desc:
              "Share homework, notices, attendance, results, and fee updates through the parent mobile app instead of scattered messages.",
          },
          {
            title: "Know What Is Happening in Every Class",
            desc:
              "See classwise attendance, syllabus progress, fee status, and academic activity without waiting for manual reports.",
          },
          {
            title: "Focus on Every Student",
            desc:
              "Use individual profiles, attendance patterns, homework completion, subject-wise performance, and intervention visibility to identify who needs attention earlier.",
          },
          {
            title: "Finish Reports and Certificates Quickly",
            desc:
              "Create branded certificates, marksheets, reports, and school documents without repetitive manual formatting.",
          },
          {
            title: "Stay Compliance Ready",
            desc:
              "Keep DPDP, UDISE+, NEP, child protection, staff, and audit data organized for when your school needs it.",
          },
        ],
      },
      contact: {
        badge: "Free School Demo",
        title: "Book Your Free School Demo",
        subtitle:
          "Share your phone number and school details. Our team will contact you on call or WhatsApp to schedule a 10-minute walkthrough.",
        success: "Thank you! We've received your request. Our team will reach out shortly.",
        namePlaceholder: "Your Name *",
        phonePlaceholder: "Phone / WhatsApp Number *",
        schoolPlaceholder: "School Name *",
        emailPlaceholder: "Email Address (optional)",
        messagePlaceholder:
          "Tell us what you want to improve first: attendance, homework, fees, parent communication, or reports...",
        fallbackError: "Something went wrong. Please try again.",
        networkError: "Network error. Please check your connection and try again.",
        sending: "Sending...",
        bookMyDemo: "Book My Free Demo",
        preferWhatsapp: "Prefer WhatsApp?",
        whatsappHelp: "Message us directly and we will help you schedule a demo or one-class pilot for your school.",
        chatWhatsapp: "Chat on WhatsApp",
        responseTime: "Response Time",
        responseTimeValue: "We respond within 24 hours",
      },
      trust: {
        badge: "Early School Pilot",
        titlePrefix: "Now Onboarding Selected Schools for Our",
        titleHighlight: "Early Pilot",
        subtitle:
          "Start with one class, get hands-on setup support, and help shape SpellStudy into a platform built around the real needs of Indian schools.",
        stats: ["Schools Onboarded", "Class Pilot to Start", "Setup Target", "Hands-On Support"],
        badges: ["NEP 2020 Ready", "DPDP Ready", "AWS Hosted", "Made in India"],
        proofCards: [
          {
            title: "Parent Communication",
            desc: "Schools use SpellStudy to send attendance, homework, notices, fee updates, and reports to parents.",
          },
          {
            title: "Fee Visibility",
            desc: "Admins can see collection, pending amounts, overdue students, and daily finance activity in one place.",
          },
          {
            title: "Principal Dashboard",
            desc: "School leaders get classwise visibility into attendance, fees, syllabus, and student progress.",
          },
        ],
      },
      footer: {
        description:
          "The all-in-one school management platform built for Indian schools. Improve student outcomes, simplify operations, and stay fully compliant.",
        product: "Product",
        features: "Features",
        screenshots: "Screenshots",
        security: "Security",
        company: "Company",
        privacyPolicy: "Privacy Policy",
        contactUs: "Contact Us",
        contact: "Contact",
        allRightsReserved: "All rights reserved.",
        madeInIndia: "Made with care in India",
      },
      sticky: {
        demo: "Demo",
        whatsapp: "WhatsApp",
      },
      faq: {
        badge: "FAQ",
        title: "Frequently Asked Questions",
        subtitle: "Everything you need to know about getting started with SpellStudy for your school.",
        showMore: "Show All 20 Questions",
        showLess: "Show Fewer Questions",
        cta: "Book Your Free Demo",
        items: [
          { q: "Do we need to shift our full school to SpellStudy immediately?", a: "No. You can start with only one class first. This helps your school test attendance, homework, notices, parent updates, and basic reports without changing your full existing system immediately." },
          { q: "What is the one-class pilot?", a: "The one-class pilot is a low-risk way to try SpellStudy with one class, a few teachers, and a small group of students/parents. Your school can test the workflow, collect feedback, and expand only when your team is comfortable." },
          { q: "How long does setup take?", a: "For a one-class pilot, the setup target is around 48 hours after we receive the required details such as class, section, teacher list, student list, and basic school information." },
          { q: "What support do we get during setup?", a: "SpellStudy provides guided setup support, onboarding help, and teacher training. We help configure the class, users, student data, and basic workflows so your school can start smoothly." },
          { q: "Will teachers need technical knowledge to use SpellStudy?", a: "No advanced technical knowledge is required. Teachers can use simple workflows for attendance, homework, notices, exams, and class updates. We also provide hands-on training before going live." },
          { q: "What can parents see in the parent mobile app?", a: "Parents can view student-related updates such as attendance, homework, notices, fee updates, results, reports, and alerts, depending on the modules enabled by the school." },
          { q: "Do parents need to install an app?", a: "Yes, for the best parent experience, parents can use the mobile app to view school updates in one place. During the pilot, your school can start with a small parent group before expanding to everyone." },
          { q: "Can we use only attendance, homework, and notices first?", a: "Yes. Your school does not need to use every module from day one. You can begin with attendance, homework, notices, and parent communication, then add fees, reports, certificates, or other modules later." },
          { q: "How does SpellStudy help school owners and principals?", a: "SpellStudy gives school leaders better visibility into attendance, fees, class activity, parent communication, reports, and student progress. It helps reduce dependence on scattered registers, Excel sheets, and repeated manual follow-ups." },
          { q: "How does SpellStudy help improve student progress tracking?", a: "SpellStudy helps schools track individual student progress across attendance, homework, marks, reports, teacher observations, and performance patterns. This helps teachers and principals identify students who may need attention earlier." },
          { q: "Can SpellStudy help reduce parent follow-up calls?", a: "Yes. By sharing attendance, homework, notices, fee updates, results, and reports through a parent app, schools can reduce repeated phone calls and scattered WhatsApp follow-ups." },
          { q: "Does SpellStudy support fee tracking?", a: "Yes. SpellStudy can help schools track fee collection, pending amounts, overdue students, receipts, reminders, and daily finance activity from one place." },
          { q: "Can we generate reports, certificates, and marksheets?", a: "Yes. SpellStudy supports school documents such as reports, certificates, marksheets, and templates, helping reduce repetitive manual formatting and document preparation work." },
          { q: "Is school data secure?", a: "SpellStudy is built with role-based access, cloud hosting, backups, and privacy-focused workflows. Admins, teachers, accountants, and parents can be given access based on what they need to see." },
          { q: "Who can access school data?", a: "Access depends on user roles and permissions. School admins can control what different users such as teachers, accountants, staff members, and parents are allowed to view or manage." },
          { q: "Is SpellStudy useful if we already use WhatsApp?", a: "Yes. SpellStudy does not need to replace WhatsApp immediately. It helps organize school communication properly so attendance, homework, notices, fees, and reports are not lost across multiple chats." },
          { q: "What happens after the pilot?", a: "After the pilot, your school can review teacher adoption, parent response, and operational benefits. If your team is comfortable, you can expand SpellStudy to more classes and modules." },
          { q: "Is the demo free?", a: "Yes. You can book a free 10-minute demo to see how SpellStudy can work for your school." },
          { q: "How do we book a demo?", a: "You can book a free demo through the website form or contact the SpellStudy team directly on WhatsApp." },
          { q: "Who is SpellStudy best suited for?", a: "SpellStudy is best suited for schools that want to manage operations efficiently while also monitoring and supporting the overall development of every child. It helps schools track attendance, homework, academic progress, behaviour, participation, reports and parent communication in one connected system, so teachers and principals can identify students who need attention earlier and support each child more effectively." },
        ],
      },
    },
    dashboard: {
      welcome: "Welcome to SpellStudy",
      chooseCategory: "Choose a category to access your tools and features",
      newAdmission: "New Admission",
      addNewStudent: "Add new student",
      scanAdmit: "Scan & Admit",
      ocrFromId: "OCR from ID card",
      bulkImport: "Bulk Import",
      uploadExcelCsv: "Upload Excel or CSV",
      superTeacherSpace: "Super Teacher's Space",
      quickOverview: "Quick Overview",
      roles: {
        superuser: "Super Administrator",
        admin: "Administrator",
        principal: "Principal",
        teacher: "Teacher",
        accountant: "Accountant",
        student: "Student",
        parent: "Parent",
      },
      categories: {
        teachingHub: {
          title: "Teaching Hub",
          description: "Your complete teaching workspace - manage classes, assignments, and student progress",
        },
        userManagement: {
          title: "User Management",
          description: "Add and edit students and staff members",
        },
        admissionsCrm: {
          title: "Admissions CRM",
          description: "Admission enquiries, follow-ups, campaigns, and conversions",
        },
        academicManagement: {
          title: "Academic Management",
          description: "Classes, sessions, syllabus, and timetable",
        },
        financial: {
          title: "Financial Operations",
          description: "Fee collection, structure, offline mode, and payment settings",
        },
        hrPayroll: {
          title: "HR & Payroll",
          description: "Staff salary management, payroll processing, and staff records",
        },
        operationsErp: {
          title: "Operations & ERP",
          description: "Inventory, events, transport, library, visitors, helpdesk, health, hostel, and procurement",
        },
        inventory: {
          title: "Inventory",
          description: "Item master, stock ledger, vendors, assets, issue and return",
        },
        events: {
          title: "Events",
          description: "School events, participants, requirements, expenses, and reports",
        },
        erpExtensions: {
          title: "ERP Extensions",
          description: "Transport, library, visitors, helpdesk, health, hostel, and procurement",
        },
        reports: {
          title: "Reports & Analytics",
          description: "Academic insights, data export, and comprehensive reporting tools",
        },
        systemTools: {
          title: "Certificates",
          description: "Certificates, templates, and communication tools",
        },
        schoolSettings: {
          title: "School Settings",
          description: "School info, payment settings, and staff permissions",
        },
        compliance: {
          title: "Compliance & Regulatory",
          description:
            "DPDP consent, UDISE+, NEP HPC, financial ledger, POCSO/POSH hub, and staff credentials",
        },
        personal: {
          title: "Personal & Settings",
          description: "User profile and personal settings",
        },
      },
      tasks: {
        addStudent: ["Add New Student", "Enroll a new student into the system"],
        scanAdmission: ["Scan ID Card Admission", "Photograph an ID card to auto-fill and admit a student"],
        bulkImportStudents: ["Bulk Import Students", "Upload Excel or CSV data to create students and missing classes"],
        editStudents: ["Edit Existing Students", "Update student records and information"],
        addStaff: ["Add New Staff", "Create a new staff member account"],
        bulkImportStaff: ["Bulk Import Staff", "Upload Excel or CSV data to create staff accounts"],
        editStaff: ["Edit Existing Staff", "Update staff records and details"],
        admissionsCrm: ["Admissions CRM", "Manage admission enquiries, follow-ups, campaigns, and conversions"],
        createClass: ["Create New Class", "Add a new class to the school"],
        editClass: ["Edit Existing Class", "Modify class settings and details"],
        academicSessions: ["Academic Sessions", "Manage academic years and sessions"],
        syllabus: ["Add/Edit Syllabus", "Create and update course syllabi"],
        timeTable: ["Create/Edit Time Table", "Manage class schedules and timetables"],
        createExam: ["Create Exam", "Create an exam for one or multiple class sections"],
        editExam: ["Edit Existing Exam", "Add or update exam subjects and dates"],
        hrManagement: ["HR Management", "Staff leave, HR documents, recruitment, and appraisals"],
        feeManagement: ["Fee Management", "Complete fee hub - collect, configure, reports, and offline mode"],
        salaryManagement: ["Salary & Payroll", "Staff salary processing and payment records"],
        staffBio: ["Staff Bio & Records", "Staff information and employment records"],
        classwiseDetails: ["Classwise Student Details", "View and manage student information by class"],
        classwiseStatus: ["Classwise Status", "Academic performance and attendance by class"],
        teacherAttendance: ["Teacher Attendance", "View and manage staff attendance records"],
        userLogs: ["User Activity Logs", "Audit trail of user actions in the system"],
        downloadRecords: ["Download Records", "Export and download various school records"],
        certificates: ["Manage Certificates", "Create and manage student certificates"],
        documentTemplates: ["Document Templates", "Create and manage reusable document templates"],
        schoolInfo: ["Manage School Information", "Update school details and contact information"],
        staffPermissions: ["Staff Permission Management", "Configure role-based access for staff members"],
        systemSettings: ["System Settings", "Advanced system configuration options"],
        complianceConsent: ["DPDP / Consent Management", "Manage data consent, DSAR requests, and retention policies"],
        complianceUdise: ["UDISE+ Reporting", "DCF exports, PEN management, and RTE quota tracking"],
        complianceHpc: ["NEP Holistic Progress Card", "Competency frameworks, HPC cards, and observations"],
        complianceLedger: ["Financial Audit Ledger", "Hash-chained ledger, statutory registers, and reconciliation"],
        compliancePocso: ["Child Protection (POCSO/POSH)", "Incident management, ICC roster, safety audit, and training tracker"],
        complianceHr: ["Staff Compliance & Credentials", "Qualifications, police verification, POCSO training, and NEP PD hours"],
        profile: ["My Profile", "View and update personal information"],
        myAttendance: ["My Attendance", "View your personal attendance records"],
      },
    },
    teacher: {
      liveSyllabus: "Live Syllabus",
      addAttendance: "Add Attendance",
      addDayRating: "Add Day Rating",
      addHomework: "Add Homework",
      addAdditionalRating: "Add Additional Rating",
      addExamRating: "Add Exam Rating",
      createNewExam: "Create New Exam",
      editExistingExam: "Edit Existing Exam",
      showTimeTable: "Show Time Table",
      addNotification: "Add Notification",
      myAttendance: "My Attendance",
      attendanceAdded: "Attendance added successfully!",
      homeworkAdded: "Homework added successfully!",
      additionalRatingSubmitted: "Additional rating submitted successfully!",
      examRatingSubmitted: "Exam rating submitted successfully!",
      examCreated: "Exam created successfully!",
      examUpdated: "Exam updated successfully!",
      notificationSent: "Notification sent successfully!",
      selectClassSectionSubject: "Please select Class, Section, and Subject to view Live Syllabus.",
      noFunctionsTitle: "No Teaching Functions Available",
      noFunctionsDesc:
        "You don't have permission to access any teaching functions. Contact your administrator to request access.",
      categories: {
        academic: "Academic Operations",
        assessment: "Assessment & Grading",
        examManagement: "Exam Management",
        communication: "Communication",
        personal: "My Records",
        compliance: "Child Protection & Compliance",
      },
      reportIncident: "Report Child Protection Concern",
      myCredentials: "My Credentials & Training",
    },
  },
  hi: {
    language: {
      english: "English",
      hindi: "हिंदी",
      switchLabel: "भाषा",
    },
    common: {
      back: "वापस",
      backToDashboard: "डैशबोर्ड पर वापस",
      dashboard: "डैशबोर्ड",
      login: "लॉगिन",
      logout: "लॉगआउट",
      register: "रजिस्टर",
      registerSchool: "स्कूल रजिस्टर करें",
      selectClass: "कक्षा चुनें",
      selectSection: "सेक्शन चुनें",
      selectSubject: "विषय चुनें",
      date: "तारीख",
      loadingPermissions: "अनुमतियां लोड हो रही हैं...",
      clickToAccess: "खोलने के लिए क्लिक करें",
      more: "और...",
      accessLevel: "एक्सेस स्तर",
      signOut: "साइन आउट",
      email: "ईमेल",
      phone: "फोन",
      website: "वेबसाइट",
    },
    auth: {
      emailUsername: "ईमेल/यूजरनेम",
      password: "पासवर्ड",
      forgotPassword: "पासवर्ड भूल गए?",
      noAccountRegister: "खाता नहीं है? रजिस्टर करें",
      allFieldsRequired: "सभी फील्ड जरूरी हैं!",
      sessionExpired: "सेशन समाप्त हो गया है। कृपया फिर से लॉगिन करें।",
      failedLogin: "लॉगिन विफल: {error}",
      networkError: "नेटवर्क त्रुटि। कृपया अपना इंटरनेट कनेक्शन जांचें।",
      forgotTitle: "पासवर्ड भूल गए",
      forgotHelp: "पासवर्ड रीसेट लिंक पाने के लिए अपना ईमेल डालें।",
      sending: "भेजा जा रहा है...",
      sendResetLink: "रीसेट लिंक भेजें",
      resetTitle: "पासवर्ड रीसेट करें",
      resetHelp: "नीचे नया पासवर्ड डालें।",
      newPassword: "नया पासवर्ड",
      confirmPassword: "पासवर्ड की पुष्टि करें",
      passwordsDoNotMatch: "पासवर्ड मेल नहीं खाते",
      resetting: "रीसेट हो रहा है...",
      resetError: "पासवर्ड रीसेट करने में त्रुटि। सुनिश्चित करें कि रीसेट लिंक नया है",
      resetEmailError: "रीसेट ईमेल भेजने में त्रुटि।",
    },
    register: {
      title: "रजिस्टर",
      settingUpSchool: "स्कूल सेटअप हो रहा है",
      creatingClasses: "सेक्शन A के साथ कक्षाएं बनाई जा रही हैं...",
      academicSetupFailed: "अकादमिक सेटअप विफल रहा। कृपया फिर कोशिश करें।",
      academicSetupCompleted: "अकादमिक सेटअप पूरा हुआ।",
      schoolSetupCompleted: "स्कूल सेटअप पूरा हुआ!",
      creatingSchoolAccount: "स्कूल अकाउंट बनाया जा रहा है...",
      schoolRegisteredSetupCouldNotStart: "स्कूल रजिस्टर हो गया, लेकिन सेटअप शुरू नहीं हो सका।",
      failedToRegister: "स्कूल रजिस्टर करने में विफल। कृपया फिर कोशिश करें।",
      genericError: "एक त्रुटि हुई। कृपया अपना नेटवर्क जांचें।",
      schoolRegisteredSettingUp: "स्कूल रजिस्टर हो गया। अकादमिक सेटअप हो रहा है...",
      usuallyTakesSeconds: "इसमें आमतौर पर कुछ सेकंड लगते हैं।",
      retryAcademicSetup: "अकादमिक सेटअप फिर से करें",
      schoolName: "स्कूल का नाम",
      affiliationNumber: "स्कूल संबद्धता संख्या",
      educationBoard: "शिक्षा बोर्ड",
      classesTill: "कक्षा तक",
      classLabel: "कक्षा {number}",
      medium: "माध्यम",
      adminName: "एडमिन का नाम",
      mediumHindi: "हिंदी",
      mediumEnglish: "अंग्रेजी",
    },
    thankyou: {
      title: "धन्यवाद!",
      submitted: "आपका रजिस्ट्रेशन फॉर्म सफलतापूर्वक जमा हो गया है।",
      followUp: "हम जल्द ही आपसे संपर्क करेंगे।",
      backToLogin: "लॉगिन पर वापस जाएं",
    },
    landing: {
      lx: {
        nav: {
          story: "SpellStudy क्यों",
          platform: "प्लेटफॉर्म",
          aiDemo: "AI लाइव देखें",
          roles: "आपके लिए",
        },
        hero: {
          kicker: "भारतीय स्कूलों का स्कूल ऑपरेटिंग सिस्टम",
          title1: "आपका पूरा स्कूल।",
          title2: "एक जीवंत डैशबोर्ड।",
          subtitle:
            "हाज़िरी, फीस, होमवर्क, परीक्षा, अभिभावक संपर्क और AI असिस्टेंट — स्कूल का हर काम, एक जगह जुड़ा हुआ। हिंदी और अंग्रेज़ी दोनों में।",
          ctaDemo: "फ्री डेमो बुक करें",
          ctaWhatsapp: "WhatsApp पर बात करें",
          scrollCue: "नीचे स्क्रॉल करें",
        },
        chaos: {
          kicker: "स्कूल की एक आम सुबह",
          title: "स्कूल चलाना ऐसा नहीं लगना चाहिए जैसे",
          titleHighlight: "संभाला हुआ बिखराव।",
          subtitle:
            "रजिस्टर एक कमरे में, Excel दूसरे में, फीस डायरी में, और अभिभावक बीस WhatsApp ग्रुप्स में। स्क्रॉल करते रहिए — देखिए सब कुछ एक जगह आने पर क्या होता है।",
          cards: [
            { title: "कागज़ी रजिस्टर", tag: "हाज़िरी, रिकॉर्ड — सब मैनुअल" },
            { title: "Excel शीट्स", tag: "एक गलत सेल, घंटों की मेहनत बर्बाद" },
            { title: "WhatsApp ग्रुप्स", tag: "40 चैट्स में खोई सूचनाएं" },
            { title: "फीस बकाया डायरी", tag: "किसने दी? किसने नहीं?" },
            { title: "नोटिस की फोटोकॉपी", tag: "आधी घर पहुंचती ही नहीं" },
            { title: "रिपोर्ट कार्ड की रातें", tag: "शिक्षक रात 2 बजे तक जागते हैं" },
          ],
          resolve: "एक स्क्रॉल। जब सब कुछ एक जगह हो, तो काम इतना ही तेज़ लगता है।",
        },
        pillars: {
          kicker: "प्लेटफॉर्म",
          title: "पांच स्तंभ। शून्य रजिस्टर।",
          items: [
            {
              title: "हाज़िरी और कक्षाएं",
              desc: "कक्षावार हाज़िरी और स्कूल की स्थिति, सुबह 9:05 बजे तक प्रिंसिपल की स्क्रीन पर।",
            },
            {
              title: "फीस और कलेक्शन",
              desc: "आज का कलेक्शन, बकाया राशि और फॉलो-अप — एक ही फाइनेंस डैशबोर्ड।",
            },
            {
              title: "पढ़ाई और प्रगति",
              desc: "हर विद्यार्थी के अंक, होमवर्क और प्रगति — समय रहते पहचानें किसे मदद चाहिए।",
            },
            {
              title: "शिक्षक और संवाद",
              desc: "होमवर्क, नोटिस और परिणाम हर अभिभावक के फोन तक — बिना फॉलो-अप कॉल के।",
            },
            {
              title: "AI रिपोर्ट और इनसाइट्स",
              desc: "सवाल पूछिए, रिपोर्ट पाइए। हिंदी या अंग्रेज़ी में। सेकंडों में।",
            },
          ],
        },
        ai: {
          kicker: "कमाल का पल",
          title: "मिलिए अपने स्कूल के",
          titleHighlight: "AI असिस्टेंट से।",
          subtitle:
            "यह असली रिकॉर्डिंग है — देखिए AI असिस्टेंट स्कूल का काम ठीक वैसे करता है जैसे आपकी टीम कहती।",
          chips: [
            "हिंदी या अंग्रेज़ी में पूछें",
            "कोई भी रिपोर्ट सेकंडों में",
            "“कक्षा 8 के फीस बकायादार?” — जवाब हाज़िर",
            "हर स्टाफ सदस्य के लिए आसान",
          ],
          caption: "SpellStudy की लाइव फुटेज — कोई मॉकअप नहीं।",
          unmute: "आवाज़ के लिए टैप करें",
          mute: "म्यूट",
          replay: "फिर से देखें",
          fullscreen: "फुल स्क्रीन",
          exitFullscreen: "फुल स्क्रीन बंद करें",
        },
        roles: {
          kicker: "आपकी भूमिका के लिए",
          title: "स्कूल में आप जो भी हों, यह आपके लिए है।",
          subtitle:
            "मालिक, प्रिंसिपल, शिक्षक और अभिभावक — स्क्रीन अलग-अलग, पर सच एक ही।",
          cards: [
            {
              title: "मालिक और डायरेक्टर",
              hook: "पूरा नियंत्रण, कहीं से भी",
              points: [
                "फीस कलेक्शन एक नज़र में",
                "हर कैंपस, एक लॉगिन",
                "बिना कॉल के दैनिक संचालन",
                "आपका डेटा आपका ही रहता है",
              ],
            },
            {
              title: "प्रिंसिपल",
              hook: "आपका स्कूल एक स्क्रीन पर",
              points: [
                "सुबह 9 बजे तक कक्षावार हाज़िरी",
                "शैक्षणिक प्रगति ट्रैकिंग",
                "स्टाफ और अनुपालन दृश्य",
                "रोज़ की भागदौड़ कम",
              ],
            },
            {
              title: "शिक्षक",
              hook: "पढ़ाएं ज़्यादा, टाइप करें कम",
              points: [
                "हाज़िरी सेकंडों में",
                "होमवर्क दो टैप में",
                "रिपोर्ट कार्ड बिना रतजगे के",
                "नोटिस जो सच में पहुंचते हैं",
              ],
            },
            {
              title: "अभिभावक",
              hook: "भरोसा, हर दिन",
              points: [
                "हाज़िरी और होमवर्क अपडेट",
                "फीस रसीद फोन पर",
                "परिणाम प्रकाशित होते ही",
                "एक ऐप, ग्रुप का शोर नहीं",
              ],
            },
          ],
          counters: [
            { value: 48, suffix: " घंटे", label: "विवरण मिलने से पहली लाइव कक्षा तक" },
            { value: 1, suffix: " कक्षा", label: "पायलट शुरू करने के लिए बस इतना काफी है" },
            { value: 10, suffix: " मिनट", label: "डेमो — फैसले से पहले खुद देखें" },
            { value: 24, suffix: " घंटे", label: "हमारी टीम का अधिकतम जवाब समय" },
          ],
        },
        journey: {
          kicker: "कोई जोखिम भरा माइग्रेशन नहीं",
          title: "डेमो से लाइव तक, सिर्फ",
          titleHighlight: "एक हफ्ते में।",
          subtitle:
            "एक कक्षा से शुरू करें। पूरे स्कूल का माइग्रेशन नहीं, कोई रुकावट नहीं — तभी बढ़ाएं जब आपके शिक्षक खुद कहें।",
          steps: [
            { day: "दिन 0", title: "10 मिनट का डेमो", desc: "चाय की चुस्की के साथ SpellStudy देखें — कॉल या WhatsApp पर।" },
            { day: "दिन 1–2", title: "एक कक्षा सेटअप", desc: "आपके विद्यार्थी, शिक्षक और कक्षा संरचना हम खुद इंपोर्ट करते हैं।" },
            { day: "दिन 3", title: "शिक्षक प्रशिक्षित", desc: "हैंड्स-ऑन ट्रेनिंग — हाज़िरी, होमवर्क, नोटिस मिनटों में।" },
            { day: "दिन 7", title: "अभिभावक लाइव", desc: "अभिभावकों को फोन पर अपडेट मिलने लगते हैं। फॉलो-अप कॉल बंद।" },
          ],
          cta: "मेरा एक-कक्षा पायलट शुरू करें",
        },
        cosmos: {
          mission: "मिशन: पृथ्वी → सूर्य",
          earth: { name: "पृथ्वी", tag: "लॉन्च पैड — आपका स्कूल आज" },
          moon: { name: "चंद्रमा", tag: "कागज़ी कामकाज का अंधेरा पक्ष" },
          mars: { name: "मंगल", tag: "नई दुनिया के पांच स्तंभ" },
          jupiter: { name: "बृहस्पति", tag: "विशाल शक्ति, ज़रा-सी मेहनत" },
          saturn: { name: "शनि", tag: "हर भूमिका सही कक्षा में" },
          venus: { name: "शुक्र", tag: "7 दिन में आरामदायक लैंडिंग" },
          mercury: { name: "बुध", tag: "तेज़ जवाब, छोटी कक्षाएं" },
          sun: { name: "सूर्य", tag: "जहां आपका स्कूल चमकता है" },
        },
        finale: {
          title1: "आपका स्कूल हकदार है",
          title2: "रजिस्टरों से बेहतर का।",
          subtitle:
            "उन स्कूलों में शामिल हों जो अपना अगला दशक SpellStudy पर बना रहे हैं। फर्क देखने के लिए 10 मिनट का डेमो काफी है।",
          ctaDemo: "मेरा फ्री डेमो बुक करें",
          ctaWhatsapp: "WhatsApp पर बात करें",
          footnote: "फ्री डेमो • एक-कक्षा पायलट • सेटअप सहायता शामिल • मेड इन इंडिया",
        },
      },
      nav: {
        pilot: "पायलट",
        roles: "भूमिकाएं",
        product: "प्रोडक्ट",
        howItWorks: "कैसे काम करता है",
        contact: "संपर्क",
        login: "लॉगिन",
        bookDemo: "फ्री डेमो बुक करें",
        openNavigation: "नेविगेशन खोलें",
        oneClassPilot: "एक-कक्षा पायलट",
        roleBenefits: "भूमिका लाभ",
        productScreenshots: "प्रोडक्ट स्क्रीनशॉट",
        whatsAppDemo: "WhatsApp पर डेमो",
      },
      launch: {
        offer: "विशेष लॉन्च ऑफर: पहले 100 स्कूलों के लिए फ्री सेटअप। एक कक्षा से शुरू करें।",
        bookDemo: "फ्री डेमो बुक करें",
        whatsAppDemo: "WhatsApp पर डेमो",
      },
      hero: {
        badge: "भारतीय स्कूलों के लिए बनाया गया",
        titlePrefix: "स्कूल संचालन और विद्यार्थी प्रगति संभालने के लिए",
        titleHighlight: "एक ही प्लेटफॉर्म",
        subtitle:
          "एक आसान डैशबोर्ड और पैरेंट मोबाइल ऐप से अटेंडेंस, होमवर्क, फीस, नोटिस, रिपोर्ट, अभिभावक संवाद और विद्यार्थी प्रगति संभालें।",
        offer: "पहले 100 स्कूलों के लिए फ्री सेटअप। केवल एक कक्षा से शुरू करें, पूरे स्कूल का माइग्रेशन जरूरी नहीं।",
        bookDemo: "फ्री 10-मिनट डेमो बुक करें",
        whatsAppDemo: "WhatsApp पर डेमो",
        trust: "एक कक्षा से शुरू करें • सेटअप सपोर्ट शामिल • AWS होस्टेड",
        imageAlt: "SpellStudy एडमिन डैशबोर्ड",
        pillars: [
          "विद्यार्थी प्रगति ट्रैकिंग",
          "अटेंडेंस और होमवर्क",
          "फीस फॉलो-अप",
          "पैरेंट मोबाइल ऐप",
        ],
        playVideo: "वीडियो चलाएं",
        closeVideo: "वीडियो बंद करें",
        unmute: "आवाज़ के लिए टैप करें",
        mute: "म्यूट",
        replay: "फिर से देखें",
        fullscreen: "फुल स्क्रीन",
        exitFullscreen: "फुल स्क्रीन बंद करें",
      },
      pilot: {
        badge: "कम जोखिम वाला पायलट",
        title: "पूरे स्कूल सिस्टम को बदले बिना SpellStudy आजमाएं",
        subtitle: "छोटे स्तर से शुरू करें, पैरेंट रिस्पॉन्स और शिक्षक अपनाने को देखें, फिर टीम तैयार होने पर विस्तार करें।",
        cta: "फ्री एक-कक्षा पायलट शुरू करें",
        items: [
          "1 कक्षा से शुरू करें",
          "2 शिक्षक जोड़ें",
          "20-40 विद्यार्थी इम्पोर्ट करें",
          "होमवर्क, अटेंडेंस और नोटिस भेजें",
          "माता-पिता मोबाइल पर अपडेट देखें",
          "सेटअप सपोर्ट शामिल",
        ],
      },
      roles: {
        badge: "स्कूल के हर हितधारक के लिए",
        title: "एक प्लेटफॉर्म, हर भूमिका के लिए स्पष्ट लाभ",
        subtitle:
          "मालिकों और प्रिंसिपलों को दृश्यता मिलती है, शिक्षकों का समय बचता है, और अभिभावक बार-बार कॉल किए बिना अपडेट रहते हैं।",
        cards: [
          { title: "एडमिन टीमों के लिए", points: ["फीस", "विद्यार्थी रिकॉर्ड", "रिपोर्ट", "सर्टिफिकेट"] },
          { title: "शिक्षकों के लिए", points: ["अटेंडेंस", "होमवर्क", "परीक्षाएं", "नोटिस"] },
          { title: "अभिभावकों के लिए", points: ["होमवर्क", "अटेंडेंस", "फीस अपडेट", "रिपोर्ट"] },
          {
            title: "प्रिंसिपल और डायरेक्टर के लिए",
            points: ["डैशबोर्ड", "स्कूल दृश्यता", "अभिभावक संतुष्टि", "कक्षा प्रगति"],
          },
        ],
      },
      pain: {
        badge: "चुनौती",
        titlePrefix: "आज स्कूल चलाना",
        titleHighlight: "पहले से कठिन है",
        subtitle:
          "अधिकतर स्कूलों को और जटिल सॉफ्टवेयर नहीं चाहिए। उन्हें कम पैरेंट फॉलो-अप, तेज फीस ट्रैकिंग और दैनिक संचालन के लिए एक जगह चाहिए।",
        cards: [
          {
            title: "माता-पिता अपडेट के लिए बार-बार कॉल करते हैं",
            desc:
              "होमवर्क, अटेंडेंस, नोटिस, फीस रिमाइंडर और रिजल्ट के लिए स्टाफ को अक्सर बार-बार कॉल या WhatsApp फॉलो-अप करना पड़ता है।",
          },
          {
            title: "जिन विद्यार्थियों को मदद चाहिए वे देर से पहचाने जाते हैं",
            desc:
              "जब अटेंडेंस, होमवर्क, टेस्ट और शिक्षक अवलोकन बिखरे होते हैं, तो शुरुआती संकेत छूट जाते हैं।",
          },
          {
            title: "फीस फॉलो-अप में बहुत समय लगता है",
            desc:
              "जब खाते रजिस्टर या स्प्रेडशीट में हों, तो लंबित फीस, दैनिक कलेक्शन, रसीद और रिमाइंडर ट्रैक करना कठिन होता है।",
          },
          {
            title: "होमवर्क और नोटिस छूट जाते हैं",
            desc:
              "शिक्षक अलग-अलग जगह अपडेट भेजते हैं, अभिभावक संदेश मिस करते हैं, और स्कूल के पास साझा जानकारी का स्पष्ट रिकॉर्ड नहीं रहता।",
          },
          {
            title: "मैनुअल रजिस्टर काम धीमा करते हैं",
            desc:
              "विद्यार्थी रिकॉर्ड, अटेंडेंस, रिपोर्ट, सर्टिफिकेट और कक्षा डेटा कागज और Excel शीटों में बिखरे रहते हैं।",
          },
          {
            title: "कम्प्लायंस और रिपोर्ट का दबाव बढ़ता है",
            desc:
              "स्कूल डेटा व्यवस्थित न हो तो ऑडिट, रिपोर्ट, दस्तावेज और कम्प्लायंस काम आखिरी समय में तनावपूर्ण हो जाते हैं।",
          },
        ],
      },
      features: {
        badge: "समाधान",
        titlePrefix: "आपके स्कूल की हर जरूरत,",
        titleHighlight: "एक प्लेटफॉर्म",
        subtitle:
          "SpellStudy मैनुअल रजिस्टर, Excel शीट और बिखरे पैरेंट मैसेज की जगह एक जुड़ा हुआ स्कूल ऑपरेटिंग सिस्टम देता है।",
        cards: [
          {
            title: "फीस तेजी से कलेक्ट करें",
            desc:
              "आज का कलेक्शन, लंबित राशि, भुगतान करने वाले विद्यार्थी, ओवरड्यू खाते और रिमाइंडर एक फाइनेंस डैशबोर्ड से देखें।",
          },
          {
            title: "पैरेंट फॉलो-अप कॉल कम करें",
            desc:
              "बिखरे संदेशों की जगह पैरेंट मोबाइल ऐप से होमवर्क, नोटिस, अटेंडेंस, रिजल्ट और फीस अपडेट साझा करें।",
          },
          {
            title: "हर कक्षा में क्या हो रहा है जानें",
            desc:
              "मैनुअल रिपोर्ट का इंतजार किए बिना कक्षा-वार अटेंडेंस, सिलेबस प्रगति, फीस स्थिति और अकादमिक गतिविधि देखें।",
          },
          {
            title: "हर विद्यार्थी पर ध्यान दें",
            desc:
              "व्यक्तिगत प्रोफाइल, अटेंडेंस पैटर्न, होमवर्क पूरा होना और विषय-वार प्रदर्शन से जल्दी पहचानें कि किसे ध्यान चाहिए।",
          },
          {
            title: "रिपोर्ट और सर्टिफिकेट जल्दी बनाएं",
            desc:
              "बार-बार मैनुअल फॉर्मैटिंग किए बिना ब्रांडेड सर्टिफिकेट, मार्कशीट, रिपोर्ट और स्कूल दस्तावेज बनाएं।",
          },
          {
            title: "कम्प्लायंस के लिए तैयार रहें",
            desc:
              "DPDP, UDISE+, NEP, चाइल्ड प्रोटेक्शन, स्टाफ और ऑडिट डेटा व्यवस्थित रखें।",
          },
        ],
      },
      contact: {
        badge: "फ्री स्कूल डेमो",
        title: "अपना फ्री स्कूल डेमो बुक करें",
        subtitle:
          "अपना फोन नंबर और स्कूल विवरण साझा करें। हमारी टीम 10-मिनट वॉकथ्रू शेड्यूल करने के लिए कॉल या WhatsApp पर संपर्क करेगी।",
        success: "धन्यवाद! हमें आपका अनुरोध मिल गया है। हमारी टीम जल्द संपर्क करेगी।",
        namePlaceholder: "आपका नाम *",
        phonePlaceholder: "फोन / WhatsApp नंबर *",
        schoolPlaceholder: "स्कूल का नाम *",
        emailPlaceholder: "ईमेल पता (वैकल्पिक)",
        messagePlaceholder:
          "बताएं कि आप पहले क्या सुधारना चाहते हैं: अटेंडेंस, होमवर्क, फीस, पैरेंट कम्युनिकेशन या रिपोर्ट...",
        fallbackError: "कुछ गलत हुआ। कृपया फिर कोशिश करें।",
        networkError: "नेटवर्क त्रुटि। कृपया कनेक्शन जांचें और फिर कोशिश करें।",
        sending: "भेजा जा रहा है...",
        bookMyDemo: "मेरा फ्री डेमो बुक करें",
        preferWhatsapp: "WhatsApp पसंद है?",
        whatsappHelp: "हमें सीधे संदेश भेजें और हम आपके स्कूल के लिए डेमो या एक-कक्षा पायलट शेड्यूल करने में मदद करेंगे।",
        chatWhatsapp: "WhatsApp पर चैट करें",
        responseTime: "जवाब का समय",
        responseTimeValue: "हम 24 घंटे के भीतर जवाब देते हैं",
      },
      trust: {
        badge: "अर्ली स्कूल पायलट",
        titlePrefix: "हम चुनिंदा स्कूलों को",
        titleHighlight: "अर्ली पायलट",
        subtitle:
          "एक कक्षा से शुरू करें, हैंड्स-ऑन सेटअप सपोर्ट पाएं, और भारतीय स्कूलों की वास्तविक जरूरतों के अनुसार SpellStudy को आकार देने में मदद करें।",
        stats: ["ऑनबोर्डेड स्कूल", "शुरू करने के लिए कक्षा पायलट", "सेटअप लक्ष्य", "हैंड्स-ऑन सपोर्ट"],
        badges: ["NEP 2020 तैयार", "DPDP तैयार", "AWS होस्टेड", "भारत में निर्मित"],
        proofCards: [
          {
            title: "पैरेंट कम्युनिकेशन",
            desc: "स्कूल SpellStudy से अटेंडेंस, होमवर्क, नोटिस, फीस अपडेट और रिपोर्ट अभिभावकों को भेजते हैं।",
          },
          {
            title: "फीस दृश्यता",
            desc: "एडमिन कलेक्शन, लंबित राशि, ओवरड्यू विद्यार्थी और दैनिक फाइनेंस गतिविधि एक जगह देख सकते हैं।",
          },
          {
            title: "प्रिंसिपल डैशबोर्ड",
            desc: "स्कूल लीडर्स को अटेंडेंस, फीस, सिलेबस और विद्यार्थी प्रगति की कक्षा-वार दृश्यता मिलती है।",
          },
        ],
      },
      footer: {
        description:
          "भारतीय स्कूलों के लिए बना ऑल-इन-वन स्कूल मैनेजमेंट प्लेटफॉर्म। विद्यार्थी परिणाम सुधारें, संचालन सरल करें और कम्प्लायंस के लिए तैयार रहें।",
        product: "प्रोडक्ट",
        features: "फीचर्स",
        screenshots: "स्क्रीनशॉट",
        security: "सुरक्षा",
        company: "कंपनी",
        privacyPolicy: "प्राइवेसी पॉलिसी",
        contactUs: "संपर्क करें",
        contact: "संपर्क",
        allRightsReserved: "सर्वाधिकार सुरक्षित।",
        madeInIndia: "भारत में देखभाल के साथ बनाया गया",
      },
      sticky: {
        demo: "डेमो",
        whatsapp: "WhatsApp",
      },
      faq: {
        badge: "अक्सर पूछे जाने वाले सवाल",
        title: "अक्सर पूछे जाने वाले सवाल",
        subtitle: "अपने स्कूल के लिए SpellStudy शुरू करने के बारे में आपको जो कुछ भी जानना चाहिए।",
        showMore: "सभी 20 सवाल देखें",
        showLess: "कम सवाल देखें",
        cta: "अपना फ्री डेमो बुक करें",
        items: [
          { q: "क्या हमें तुरंत अपने पूरे स्कूल को SpellStudy पर शिफ्ट करना होगा?", a: "नहीं। आप पहले केवल एक कक्षा से शुरू कर सकते हैं। इससे आपका स्कूल बिना मौजूदा सिस्टम बदले अटेंडेंस, होमवर्क, नोटिस, पैरेंट अपडेट और बेसिक रिपोर्ट टेस्ट कर सकता है।" },
          { q: "एक-कक्षा पायलट क्या है?", a: "एक-कक्षा पायलट एक कम जोखिम वाला तरीका है जिसमें आप एक कक्षा, कुछ शिक्षकों और छात्रों/अभिभावकों के छोटे समूह के साथ SpellStudy आज़मा सकते हैं। आपका स्कूल वर्कफ़्लो टेस्ट कर सकता है, फ़ीडबैक ले सकता है, और टीम सहज होने पर ही विस्तार कर सकता है।" },
          { q: "सेटअप में कितना समय लगता है?", a: "एक-कक्षा पायलट के लिए, ज़रूरी विवरण जैसे कक्षा, सेक्शन, शिक्षक सूची, छात्र सूची और बेसिक स्कूल जानकारी मिलने के बाद सेटअप का लक्ष्य लगभग 48 घंटे है।" },
          { q: "सेटअप के दौरान हमें क्या सपोर्ट मिलता है?", a: "SpellStudy गाइडेड सेटअप सपोर्ट, ऑनबोर्डिंग हेल्प और शिक्षक ट्रेनिंग देता है। हम कक्षा, यूज़र, छात्र डेटा और बेसिक वर्कफ़्लो कॉन्फ़िगर करने में मदद करते हैं ताकि आपका स्कूल आसानी से शुरू कर सके।" },
          { q: "क्या शिक्षकों को SpellStudy इस्तेमाल करने के लिए तकनीकी ज्ञान चाहिए?", a: "नहीं, किसी एडवांस तकनीकी ज्ञान की ज़रूरत नहीं है। शिक्षक अटेंडेंस, होमवर्क, नोटिस, परीक्षा और कक्षा अपडेट के लिए सरल वर्कफ़्लो इस्तेमाल कर सकते हैं। हम लाइव होने से पहले हैंड्स-ऑन ट्रेनिंग भी देते हैं।" },
          { q: "पैरेंट मोबाइल ऐप में माता-पिता क्या देख सकते हैं?", a: "माता-पिता छात्र से जुड़े अपडेट जैसे अटेंडेंस, होमवर्क, नोटिस, फीस अपडेट, रिज़ल्ट, रिपोर्ट और अलर्ट देख सकते हैं, जो स्कूल द्वारा सक्षम मॉड्यूल पर निर्भर करता है।" },
          { q: "क्या माता-पिता को ऐप इंस्टॉल करना होगा?", a: "हाँ, सबसे अच्छे अनुभव के लिए माता-पिता मोबाइल ऐप से स्कूल अपडेट एक जगह देख सकते हैं। पायलट के दौरान आपका स्कूल सबको शामिल करने से पहले एक छोटे पैरेंट ग्रुप से शुरू कर सकता है।" },
          { q: "क्या हम पहले सिर्फ अटेंडेंस, होमवर्क और नोटिस इस्तेमाल कर सकते हैं?", a: "हाँ। आपके स्कूल को पहले दिन से हर मॉड्यूल इस्तेमाल करने की ज़रूरत नहीं है। आप अटेंडेंस, होमवर्क, नोटिस और पैरेंट कम्युनिकेशन से शुरू कर सकते हैं, फिर बाद में फीस, रिपोर्ट, सर्टिफिकेट या अन्य मॉड्यूल जोड़ सकते हैं।" },
          { q: "SpellStudy स्कूल मालिकों और प्रिंसिपलों की कैसे मदद करता है?", a: "SpellStudy स्कूल लीडर्स को अटेंडेंस, फीस, कक्षा गतिविधि, पैरेंट कम्युनिकेशन, रिपोर्ट और छात्र प्रगति की बेहतर दृश्यता देता है। यह बिखरे रजिस्टर, Excel शीट और बार-बार मैनुअल फॉलो-अप पर निर्भरता कम करने में मदद करता है।" },
          { q: "SpellStudy छात्र प्रगति ट्रैकिंग में कैसे सुधार करता है?", a: "SpellStudy स्कूलों को अटेंडेंस, होमवर्क, मार्क्स, रिपोर्ट, शिक्षक अवलोकन और प्रदर्शन पैटर्न के ज़रिए व्यक्तिगत छात्र प्रगति ट्रैक करने में मदद करता है। इससे शिक्षक और प्रिंसिपल जिन छात्रों को ध्यान चाहिए उन्हें जल्दी पहचान सकते हैं।" },
          { q: "क्या SpellStudy पैरेंट फॉलो-अप कॉल कम करने में मदद कर सकता है?", a: "हाँ। पैरेंट ऐप के ज़रिए अटेंडेंस, होमवर्क, नोटिस, फीस अपडेट, रिज़ल्ट और रिपोर्ट साझा करके स्कूल बार-बार फोन कॉल और बिखरे WhatsApp फॉलो-अप कम कर सकते हैं।" },
          { q: "क्या SpellStudy फीस ट्रैकिंग सपोर्ट करता है?", a: "हाँ। SpellStudy स्कूलों को फीस कलेक्शन, लंबित राशि, ओवरड्यू छात्र, रसीद, रिमाइंडर और दैनिक फाइनेंस गतिविधि एक जगह से ट्रैक करने में मदद कर सकता है।" },
          { q: "क्या हम रिपोर्ट, सर्टिफिकेट और मार्कशीट जनरेट कर सकते हैं?", a: "हाँ। SpellStudy रिपोर्ट, सर्टिफिकेट, मार्कशीट और टेम्पलेट जैसे स्कूल दस्तावेज़ सपोर्ट करता है, जो बार-बार मैनुअल फॉर्मैटिंग और दस्तावेज़ तैयारी का काम कम करने में मदद करता है।" },
          { q: "क्या स्कूल डेटा सुरक्षित है?", a: "SpellStudy रोल-बेस्ड एक्सेस, क्लाउड होस्टिंग, बैकअप और प्राइवेसी-फोकस्ड वर्कफ़्लो के साथ बनाया गया है। एडमिन, शिक्षक, अकाउंटेंट और माता-पिता को उनकी ज़रूरत के अनुसार एक्सेस दिया जा सकता है।" },
          { q: "स्कूल डेटा कौन एक्सेस कर सकता है?", a: "एक्सेस यूज़र रोल और परमिशन पर निर्भर करता है। स्कूल एडमिन कंट्रोल कर सकते हैं कि शिक्षक, अकाउंटेंट, स्टाफ और माता-पिता जैसे विभिन्न यूज़र क्या देख या मैनेज कर सकते हैं।" },
          { q: "क्या SpellStudy उपयोगी है अगर हम पहले से WhatsApp इस्तेमाल करते हैं?", a: "हाँ। SpellStudy को तुरंत WhatsApp रिप्लेस करने की ज़रूरत नहीं है। यह स्कूल कम्युनिकेशन को व्यवस्थित करने में मदद करता है ताकि अटेंडेंस, होमवर्क, नोटिस, फीस और रिपोर्ट कई चैट में खो न जाएं।" },
          { q: "पायलट के बाद क्या होता है?", a: "पायलट के बाद आपका स्कूल शिक्षक अपनाने, पैरेंट रिस्पॉन्स और ऑपरेशनल लाभ की समीक्षा कर सकता है। अगर आपकी टीम सहज है, तो आप SpellStudy को अधिक कक्षाओं और मॉड्यूल में विस्तारित कर सकते हैं।" },
          { q: "क्या डेमो फ्री है?", a: "हाँ। आप फ्री 10-मिनट डेमो बुक करके देख सकते हैं कि SpellStudy आपके स्कूल के लिए कैसे काम कर सकता है।" },
          { q: "हम डेमो कैसे बुक करें?", a: "आप वेबसाइट फॉर्म के ज़रिए या सीधे WhatsApp पर SpellStudy टीम से संपर्क करके फ्री डेमो बुक कर सकते हैं।" },
          { q: "SpellStudy किन स्कूलों के लिए सबसे उपयुक्त है?", a: "SpellStudy उन स्कूलों के लिए सबसे उपयुक्त है जो संचालन को कुशलतापूर्वक मैनेज करने के साथ-साथ हर बच्चे के समग्र विकास की निगरानी और सहायता भी करना चाहते हैं। यह स्कूलों को अटेंडेंस, होमवर्क, अकादमिक प्रगति, व्यवहार, भागीदारी, रिपोर्ट और पैरेंट कम्युनिकेशन को एक जुड़े सिस्टम में ट्रैक करने में मदद करता है, ताकि शिक्षक और प्रिंसिपल जिन छात्रों को ध्यान चाहिए उन्हें जल्दी पहचान सकें और हर बच्चे को प्रभावी रूप से सहायता दे सकें।" },
        ],
      },
    },
    dashboard: {
      welcome: "SpellStudy में आपका स्वागत है",
      chooseCategory: "अपने टूल और फीचर खोलने के लिए श्रेणी चुनें",
      newAdmission: "नया प्रवेश",
      addNewStudent: "नया विद्यार्थी जोड़ें",
      scanAdmit: "स्कैन और एडमिट",
      ocrFromId: "ID कार्ड से OCR",
      bulkImport: "बल्क इम्पोर्ट",
      uploadExcelCsv: "Excel या CSV अपलोड करें",
      superTeacherSpace: "सुपर टीचर स्पेस",
      quickOverview: "त्वरित अवलोकन",
      roles: {
        superuser: "सुपर एडमिनिस्ट्रेटर",
        admin: "एडमिनिस्ट्रेटर",
        principal: "प्रिंसिपल",
        teacher: "शिक्षक",
        accountant: "अकाउंटेंट",
        student: "विद्यार्थी",
        parent: "अभिभावक",
      },
      categories: {
        teachingHub: {
          title: "टीचिंग हब",
          description: "आपका पूरा शिक्षण कार्यक्षेत्र - कक्षाएं, असाइनमेंट और विद्यार्थी प्रगति संभालें",
        },
        userManagement: {
          title: "यूजर मैनेजमेंट",
          description: "विद्यार्थी और स्टाफ जोड़ें और संपादित करें",
        },
        admissionsCrm: {
          title: "एडमिशन CRM",
          description: "एडमिशन enquiries, follow-ups, campaigns और conversions संभालें",
        },
        academicManagement: {
          title: "अकादमिक मैनेजमेंट",
          description: "कक्षाएं, सत्र, सिलेबस और टाइमटेबल",
        },
        financial: {
          title: "फाइनेंशियल ऑपरेशंस",
          description: "फीस कलेक्शन, संरचना, ऑफलाइन मोड और भुगतान सेटिंग",
        },
        hrPayroll: {
          title: "HR और पेरोल",
          description: "स्टाफ वेतन प्रबंधन, पेरोल प्रोसेसिंग और स्टाफ रिकॉर्ड",
        },
        operationsErp: {
          title: "ऑपरेशंस और ERP",
          description: "इन्वेंटरी, इवेंट्स, transport, library, visitors, helpdesk, health, hostel और procurement",
        },
        inventory: {
          title: "इन्वेंटरी",
          description: "आइटम मास्टर, स्टॉक लेजर, वेंडर, एसेट, इश्यू और रिटर्न",
        },
        events: {
          title: "इवेंट्स",
          description: "स्कूल इवेंट, प्रतिभागी, आवश्यकताएं, खर्च और रिपोर्ट",
        },
        erpExtensions: {
          title: "ERP एक्सटेंशन",
          description: "Transport, library, visitors, helpdesk, health, hostel और procurement",
        },
        reports: {
          title: "रिपोर्ट और एनालिटिक्स",
          description: "अकादमिक इनसाइट, डेटा एक्सपोर्ट और व्यापक रिपोर्टिंग टूल",
        },
        systemTools: {
          title: "सर्टिफिकेट्स",
          description: "सर्टिफिकेट, टेम्पलेट और कम्युनिकेशन टूल",
        },
        schoolSettings: {
          title: "स्कूल सेटिंग",
          description: "स्कूल जानकारी, भुगतान सेटिंग और स्टाफ अनुमतियां",
        },
        compliance: {
          title: "कम्प्लायंस और रेगुलेटरी",
          description:
            "DPDP consent, UDISE+, NEP HPC, फाइनेंशियल लेजर, POCSO/POSH hub और स्टाफ credentials",
        },
        personal: {
          title: "पर्सनल और सेटिंग",
          description: "यूजर प्रोफाइल और व्यक्तिगत सेटिंग",
        },
      },
      tasks: {
        addStudent: ["नया विद्यार्थी जोड़ें", "सिस्टम में नया विद्यार्थी नामांकित करें"],
        scanAdmission: ["ID कार्ड स्कैन एडमिशन", "ID कार्ड की फोटो से विद्यार्थी विवरण ऑटो-फिल करें"],
        bulkImportStudents: ["विद्यार्थी बल्क इम्पोर्ट", "Excel या CSV डेटा से विद्यार्थी और मिसिंग कक्षाएं बनाएं"],
        editStudents: ["मौजूदा विद्यार्थी संपादित करें", "विद्यार्थी रिकॉर्ड और जानकारी अपडेट करें"],
        addStaff: ["नया स्टाफ जोड़ें", "नया स्टाफ अकाउंट बनाएं"],
        bulkImportStaff: ["स्टाफ बल्क इम्पोर्ट", "Excel या CSV डेटा से स्टाफ अकाउंट बनाएं"],
        editStaff: ["मौजूदा स्टाफ संपादित करें", "स्टाफ रिकॉर्ड और विवरण अपडेट करें"],
        admissionsCrm: ["एडमिशन CRM", "एडमिशन enquiries, follow-ups, campaigns और conversions संभालें"],
        createClass: ["नई कक्षा बनाएं", "स्कूल में नई कक्षा जोड़ें"],
        editClass: ["मौजूदा कक्षा संपादित करें", "कक्षा सेटिंग और विवरण बदलें"],
        academicSessions: ["अकादमिक सत्र", "अकादमिक वर्ष और सत्र संभालें"],
        syllabus: ["सिलेबस जोड़ें/संपादित करें", "कोर्स सिलेबस बनाएं और अपडेट करें"],
        timeTable: ["टाइमटेबल बनाएं/संपादित करें", "कक्षा शेड्यूल और टाइमटेबल संभालें"],
        createExam: ["परीक्षा बनाएं", "एक या कई कक्षा-सेक्शन के लिए परीक्षा बनाएं"],
        editExam: ["मौजूदा परीक्षा संपादित करें", "परीक्षा विषय और तारीखें जोड़ें या अपडेट करें"],
        hrManagement: ["HR मैनेजमेंट", "स्टाफ leave, HR documents, recruitment और appraisals"],
        feeManagement: ["फीस मैनेजमेंट", "पूरा फीस हब - कलेक्शन, कॉन्फिगरेशन, रिपोर्ट और ऑफलाइन मोड"],
        salaryManagement: ["वेतन और पेरोल", "स्टाफ वेतन प्रोसेसिंग और भुगतान रिकॉर्ड"],
        staffBio: ["स्टाफ बायो और रिकॉर्ड", "स्टाफ जानकारी और रोजगार रिकॉर्ड"],
        classwiseDetails: ["कक्षा-वार विद्यार्थी विवरण", "कक्षा के अनुसार विद्यार्थी जानकारी देखें और संभालें"],
        classwiseStatus: ["कक्षा-वार स्थिति", "कक्षा के अनुसार अकादमिक प्रदर्शन और अटेंडेंस"],
        teacherAttendance: ["शिक्षक अटेंडेंस", "स्टाफ अटेंडेंस रिकॉर्ड देखें और संभालें"],
        userLogs: ["यूजर गतिविधि लॉग", "सिस्टम में यूजर गतिविधियों का ऑडिट ट्रेल"],
        downloadRecords: ["रिकॉर्ड डाउनलोड करें", "विभिन्न स्कूल रिकॉर्ड एक्सपोर्ट और डाउनलोड करें"],
        certificates: ["सर्टिफिकेट संभालें", "विद्यार्थी सर्टिफिकेट बनाएं और संभालें"],
        documentTemplates: ["डॉक्यूमेंट टेम्पलेट", "रीयूजेबल डॉक्यूमेंट टेम्पलेट बनाएं और संभालें"],
        schoolInfo: ["स्कूल जानकारी संभालें", "स्कूल विवरण और संपर्क जानकारी अपडेट करें"],
        staffPermissions: ["स्टाफ परमिशन मैनेजमेंट", "स्टाफ सदस्यों के लिए रोल-आधारित एक्सेस सेट करें"],
        systemSettings: ["सिस्टम सेटिंग", "एडवांस्ड सिस्टम कॉन्फिगरेशन विकल्प"],
        complianceConsent: ["DPDP / Consent Management", "डेटा consent, DSAR requests और retention policies संभालें"],
        complianceUdise: ["UDISE+ Reporting", "DCF exports, PEN management और RTE quota tracking"],
        complianceHpc: ["NEP Holistic Progress Card", "Competency frameworks, HPC cards और observations"],
        complianceLedger: ["Financial Audit Ledger", "Hash-chained ledger, statutory registers और reconciliation"],
        compliancePocso: ["Child Protection (POCSO/POSH)", "Incident management, ICC roster, safety audit और training tracker"],
        complianceHr: ["Staff Compliance & Credentials", "Qualifications, police verification, POCSO training और NEP PD hours"],
        profile: ["मेरी प्रोफाइल", "व्यक्तिगत जानकारी देखें और अपडेट करें"],
        myAttendance: ["मेरी अटेंडेंस", "अपना व्यक्तिगत अटेंडेंस रिकॉर्ड देखें"],
      },
    },
    teacher: {
      liveSyllabus: "लाइव सिलेबस",
      addAttendance: "अटेंडेंस जोड़ें",
      addDayRating: "डे रेटिंग जोड़ें",
      addHomework: "होमवर्क जोड़ें",
      addAdditionalRating: "अतिरिक्त रेटिंग जोड़ें",
      addExamRating: "परीक्षा रेटिंग जोड़ें",
      createNewExam: "नई परीक्षा बनाएं",
      editExistingExam: "मौजूदा परीक्षा संपादित करें",
      showTimeTable: "टाइमटेबल दिखाएं",
      addNotification: "नोटिफिकेशन जोड़ें",
      myAttendance: "मेरी अटेंडेंस",
      attendanceAdded: "अटेंडेंस सफलतापूर्वक जोड़ी गई!",
      homeworkAdded: "होमवर्क सफलतापूर्वक जोड़ा गया!",
      additionalRatingSubmitted: "अतिरिक्त रेटिंग सफलतापूर्वक जमा हुई!",
      examRatingSubmitted: "परीक्षा रेटिंग सफलतापूर्वक जमा हुई!",
      examCreated: "परीक्षा सफलतापूर्वक बनाई गई!",
      examUpdated: "परीक्षा सफलतापूर्वक अपडेट हुई!",
      notificationSent: "नोटिफिकेशन सफलतापूर्वक भेजा गया!",
      selectClassSectionSubject: "लाइव सिलेबस देखने के लिए कक्षा, सेक्शन और विषय चुनें।",
      noFunctionsTitle: "कोई शिक्षण फंक्शन उपलब्ध नहीं",
      noFunctionsDesc:
        "आपके पास किसी भी शिक्षण फंक्शन को खोलने की अनुमति नहीं है। एक्सेस के लिए अपने एडमिनिस्ट्रेटर से संपर्क करें।",
      categories: {
        academic: "अकादमिक ऑपरेशंस",
        assessment: "असेसमेंट और ग्रेडिंग",
        examManagement: "परीक्षा मैनेजमेंट",
        communication: "कम्युनिकेशन",
        personal: "मेरे रिकॉर्ड",
        compliance: "चाइल्ड प्रोटेक्शन और कम्प्लायंस",
      },
      reportIncident: "चाइल्ड प्रोटेक्शन चिंता रिपोर्ट करें",
      myCredentials: "मेरे credentials और training",
    },
  },
};

export const supportedLanguages = [
  { code: "en", labelKey: "language.english" },
  { code: "hi", labelKey: "language.hindi" },
];
